import React, { useState, useMemo } from 'react';
import { array, arrayOf, bool, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { pickInitialValuesForFieldSelectTree } from '../../../util/search';

import { FieldSelectTree } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectSingleFilter.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Component for the search field
const SearchField = ({ value, onChange, placeholder }) => {
  return (
    <div className={css.searchFieldWrapper}>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={css.searchField}
        autoFocus
      />
    </div>
  );
};

// Component that wraps the options with search and scroll functionality
const EnhancedSelectOptions = ({ name, options, hasSearch }) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm || !hasSearch) return options;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter(opt => {
      const label = opt.label || '';
      const option = opt.option || '';
      return (
        label.toLowerCase().includes(lowerSearchTerm) ||
        option.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [options, searchTerm, hasSearch]);

  const showNoResults = hasSearch && searchTerm && filteredOptions.length === 0;
  const needsScroll = options.length > 5;

  return (
    <div className={css.enhancedSelectWrapper}>
      {hasSearch && (
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={intl.formatMessage({ id: 'SelectSingleFilter.searchPlaceholder' })}
        />
      )}
      <div className={needsScroll ? css.scrollableOptions : css.normalOptions}>
        {showNoResults ? (
          <div className={css.noResults}>
            <FormattedMessage id="SelectSingleFilter.noMatches" />
          </div>
        ) : (
          <FieldSelectTree name={name} options={filteredOptions} />
        )}
      </div>
    </div>
  );
};

const SelectSingleFilter = props => {
  const {
    rootClassName,
    className,
    showAsPopup,
    options,
    isNestedEnum,
    id,
    name,
    label,
    queryParamNames,
    initialValues,
    contentPlacementOffset,
    onSubmit,
    ...rest
  } = props;

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];

  const classes = classNames(rootClassName || css.root, className);
  const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
  const labelForPlain = <span className={labelClass}>{label}</span>;

  // Pass the initial values with the name key so that
  // they can be passed to the correct field
  const pickedInitialValues = { [name]: pickInitialValuesForFieldSelectTree(name, initialValues) };

  // Determine if we should show search field (for filters with more than 5 options)
  // Disable search for nested/category filters as it doesn't work well with tree structure
  const hasSearch = !isNestedEnum && options && options.length > 5;

  const handleSubmit = queryParamNames => values => {
    const isArray = Array.isArray(queryParamNames);
    const hasMultipleQueryParams = isArray && queryParamNames.length > 1;
    const hasSingleQueryParam = isArray && queryParamNames.length === 1;
    const firstQueryParamName = isArray ? queryParamNames[0] : queryParamNames;

    // Nested options create multiple query params
    //   - E.g. categoryLevel1: 'v1', categoryLevel2: 'v2', categoryLevel3: 'v3'
    // If there's only a single query param in use, we flatten the value
    //   - E.g. categoryLevel: 'value'
    const usedValue = hasMultipleQueryParams
      ? queryParamNames.reduce((acc, p, i) => {
          const k = `${name}${i + 1}`;
          const v = values?.[name]?.[k] || null;
          return { ...acc, [p]: v };
        }, {})
      : hasSingleQueryParam
      ? { [firstQueryParamName]: values?.[name]?.[`${name}1`] || null }
      : values;
    onSubmit(usedValue);
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={hasSearch ? css.popupSizeLarge : css.popupSize}
      label={label}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit(queryParamNames)}
      initialValues={pickedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <EnhancedSelectOptions name={name} options={options} hasSearch={hasSearch} />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit(queryParamNames)}
      initialValues={pickedInitialValues}
      {...rest}
    >
      <EnhancedSelectOptions name={name} options={options} hasSearch={hasSearch} />
    </FilterPlain>
  );
};

SelectSingleFilter.defaultProps = {
  rootClassName: null,
  className: null,
  showAsPopup: false,
  initialValues: null,
  contentPlacementOffset: 0,
  isNestedEnum: false,
};

SelectSingleFilter.propTypes = {
  rootClassName: string,
  className: string,
  showAsPopup: bool,
  id: string.isRequired,
  name: string.isRequired,
  label: node.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  options: array.isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
  contentPlacementOffset: number,
  isNestedEnum: bool,
};

export default SelectSingleFilter;
